import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { InteractiveHoverButton } from "@/components/ui/interactive-hover-button";
import { ChatPlatformsStrip } from "@/components/marketing/chat-platforms-strip";
import {
  marketing,
  localizedHref,
  useMarketingLocale,
} from "@/i18n/marketing";

export const Hero = () => {
  const locale = useMarketingLocale();
  const t = marketing.home[locale].hero;
  // Localize internal links so localized visitors stay on their locale.
  const href = (en: string) => localizedHref(en, locale);

  return (
    <div className="flex flex-col items-center text-center py-16 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 w-full"
      >
        <h2 className="text-xl md:text-2xl font-semibold text-primary mb-3">
          {t.eyebrow}
        </h2>

        <h1 className="heading-1 mb-5 text-foreground">
          {t.headlineLine1}
          <span className="text-primary">{t.headlinePrimary}</span>
          {t.headlineLine2}
          <br />
          <span className="text-primary">{t.headlineFreePrimary}</span>
          {t.headlineLine2After}
        </h1>
        <p className="text-xl text-white dark:text-gray-200 mb-6 max-w-2xl mx-auto">
          {t.subhead}
        </p>

        <ul className="flex flex-col sm:flex-row justify-center gap-4 mb-8 max-w-lg mx-auto">
          {[t.bullet1, t.bullet2, t.bullet3].map((point, i) => (
            <li
              key={i}
              className="flex items-center gap-2 justify-center sm:justify-start text-foreground"
            >
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
              <span>{point}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
          <Link to="/login">
            <InteractiveHoverButton
              text={t.cta}
              className="w-auto min-w-[180px] text-base px-10 h-14 relative bg-opacity-0 border-primary text-foreground"
            />
          </Link>
        </div>
        <ChatPlatformsStrip label={t.worksWith} className="mt-6" />
        <div className="flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 mt-5 text-sm">
          <Link
            to={href("/rsvp-info")}
            className="font-medium text-primary hover:underline underline-offset-4"
          >
            {t.linkRsvp}
          </Link>
          <Link
            to={href("/rpe-info")}
            className="font-medium text-primary hover:underline underline-offset-4"
          >
            {t.linkRpe}
          </Link>
          <Link
            to={href("/feedback-info")}
            className="font-medium text-primary hover:underline underline-offset-4"
          >
            {t.linkFeedback}
          </Link>
        </div>
      </motion.div>
    </div>
  );
};
